/**
 * Configuration-driven settings IA: nav, icons, visibility, render contracts (opaque ids), legacy path segments.
 * No React or feature imports — implementation maps ids → components in `section_content_registry.js`.
 */
import {
  SETTINGS_MODAL_PANE_KIND,
  SETTINGS_SECTION,
  isSettingsAdminPeopleSection,
} from './constants'

export type SettingsSection = (typeof SETTINGS_SECTION)[keyof typeof SETTINGS_SECTION]

/** Column panes — registry maps these to React components. */
export type SettingsColumnPaneId =
  | 'admin_dashboard'
  | 'admin_org_branding'
  | 'admin_people'
  | 'ai_settings'
  | 'branding'
  | 'link'
  | 'login_preferences'
  | 'org_directory'
  | 'workflows'
  | 'assistant'
  | 'contacts_settings'

/** Memory shell routes — registry maps to basePath + component. */
export type SettingsMemoryRouteKey =
  | 'admin_access'
  | 'admin_billing'
  | 'admin_security'
  | 'admin_notetaker'
  | 'admin_managed_event_types'
  | 'personal_security'
  | 'personal_profile'
  | 'personal_calendar'

/**
 * Intrinsic render strategy per `?settings=` section (before runtime gates like admin nav availability).
 */
export type SettingsRenderContract =
  | { kind: typeof SETTINGS_MODAL_PANE_KIND.USERS_PAGE }
  | { kind: typeof SETTINGS_MODAL_PANE_KIND.GROUPS_SHELL }
  | { kind: typeof SETTINGS_MODAL_PANE_KIND.COLUMN; pane: SettingsColumnPaneId }
  | { kind: typeof SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE; routeKey: SettingsMemoryRouteKey }

export type SettingsModalPaneKind =
  (typeof SETTINGS_MODAL_PANE_KIND)[keyof typeof SETTINGS_MODAL_PANE_KIND]

/**
 * Static map: section string → render contract. Used by `getSettingsSectionRenderContract` and the pane registry.
 */
const SETTINGS_SECTION_RENDER: Readonly<Partial<Record<string, SettingsRenderContract>>> = {
  [SETTINGS_SECTION.profile]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'personal_profile' },

  [SETTINGS_SECTION.calendar]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'personal_calendar' },

  [SETTINGS_SECTION.branding]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'branding' },
  [SETTINGS_SECTION.link]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'link' },
  [SETTINGS_SECTION.login_preferences]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'login_preferences' },
  [SETTINGS_SECTION.workflows]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'workflows' },
  [SETTINGS_SECTION.ai_notetaker]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'ai_settings' },
  /** Legacy alias; prefer {@link SETTINGS_SECTION.ai_notetaker} + `settings_tab=callie`. */
  [SETTINGS_SECTION.assistant]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'assistant' },
  [SETTINGS_SECTION.contacts_settings]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'contacts_settings' },
  [SETTINGS_SECTION.security]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'personal_security' },

  [SETTINGS_SECTION.adminPeople]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'admin_people' },

  [SETTINGS_SECTION.adminDashboard]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'admin_dashboard' },
  [SETTINGS_SECTION.adminOrganization]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'admin_org_branding' },
  [SETTINGS_SECTION.adminBranding]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'admin_org_branding' },

  [SETTINGS_SECTION.adminAccess]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'admin_access' },
  [SETTINGS_SECTION.adminBilling]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'admin_billing' },
  [SETTINGS_SECTION.adminSecurity]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'admin_security' },
  [SETTINGS_SECTION.adminNotetaker]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'admin_notetaker' },
  [SETTINGS_SECTION.adminManagedEventTypes]: { kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE, routeKey: 'admin_managed_event_types' },

  [SETTINGS_SECTION.orgDirectory]: { kind: SETTINGS_MODAL_PANE_KIND.COLUMN, pane: 'org_directory' },
}

export function getSettingsSectionRenderContract(section: string): SettingsRenderContract | undefined {
  return SETTINGS_SECTION_RENDER[section]
}

/** Icon tokens — mapped to React components in `settings_nav_icon.js`, not imported here. */
export type SettingsIcon =
  | 'user'
  | 'calendar'
  | 'star'
  | 'palette'
  | 'link'
  | 'phone'
  | 'sparkle'
  | 'gear'
  | 'contacts'
  | 'lock'
  | 'mail'
  | 'users'
  | 'groups'
  | 'dashboard'
  | 'access'
  | 'billing'
  | 'org-branding'
  | 'organization'
  | 'domain'
  | 'admin-lock'
  | 'managed-events'

export type SettingsModalAccountNavCtx = {
  showPhoneNumbersLink: boolean
  isNotetakerEntitled: boolean
  isOptInOptOutEnabled: boolean
  isCrmImportFormsFeatureEnabled: boolean
  emailVerificationSettingsEnabled: boolean
  reactiveBlockEnabled: boolean
  isSchedulingAssistantEnabled: boolean
  isOwnerOrAdmin: boolean
  isGroupAdmin: boolean
  isNotetakerAdminSettingsPageEnabled: boolean
}

export type SettingsModalAdminNavCtx = {
  hasAdminDashboard: boolean
  isOwnerOrAdmin: boolean
  isGroupAdmin: boolean
  features: readonly string[]
  isGroupBrandingFeatureEnabled: boolean
}

export type SettingsModalAccountNavRow = {
  section: SettingsSection
  testId: string
  /** I18n key passed to `I18n.t` when building nav items */
  labelKey: string
  /** When set, modal header uses this key instead of `labelKey` (sidebar vs title copy). */
  headerKey?: string
  icon: SettingsIcon
  visibility: 'all'
  when?: (ctx: SettingsModalAccountNavCtx) => boolean
}

export type SettingsModalAdminNavRow = {
  section: SettingsSection
  testId: string
  labelKey: string
  /** When set, modal header uses this key instead of `labelKey` (sidebar vs title copy). */
  headerKey?: string
  icon: SettingsIcon
  visibility: 'admin'
  when?: (ctx: SettingsModalAdminNavCtx) => boolean
}

/**
 * Ordered “My account” nav. Visibility is enforced with `when`; `visibility: 'all'` documents IA intent.
 */
export const SETTINGS_MODAL_ACCOUNT_NAV: readonly SettingsModalAccountNavRow[] = [
  {
    section: SETTINGS_SECTION.profile,
    testId: 'settings-nav-profile',
    labelKey: 'publisher.personal_settings.navigation.profile',
    icon: 'user',
    visibility: 'all',
  },
  {
    section: SETTINGS_SECTION.calendar,
    testId: 'settings-nav-calendar',
    labelKey: 'publisher.personal_settings.calendar_section.title',
    icon: 'calendar',
    visibility: 'all',
  },
  {
    section: SETTINGS_SECTION.branding,
    testId: 'settings-nav-branding',
    labelKey: 'publisher.personal_settings.navigation.branding',
    icon: 'palette',
    visibility: 'all',
  },
  {
    section: SETTINGS_SECTION.link,
    testId: 'settings-nav-link',
    labelKey: 'publisher.personal_settings.navigation.my_link',
    icon: 'link',
    visibility: 'all',
  },
  {
    section: SETTINGS_SECTION.workflows,
    testId: 'settings-nav-workflows',
    labelKey: 'publisher.personal_settings.navigation.notifications',
    icon: 'phone',
    visibility: 'all',
    when: (ctx) => ctx.showPhoneNumbersLink,
  },
  {
    section: SETTINGS_SECTION.ai_notetaker,
    testId: 'settings-nav-ai-notetaker',
    labelKey: 'publisher.personal_settings.navigation.ai',
    headerKey: 'publisher.personal_settings.navigation.ai_settings',
    icon: 'sparkle',
    visibility: 'all',
    when: (ctx) => (
      ctx.isNotetakerEntitled
      || ctx.isSchedulingAssistantEnabled
      || (ctx.isOwnerOrAdmin && ctx.isNotetakerAdminSettingsPageEnabled)
    ),
  },
  {
    section: SETTINGS_SECTION.contacts_settings,
    testId: 'settings-nav-contacts',
    labelKey: 'publisher.left_navigation.contacts_settings',
    icon: 'contacts',
    visibility: 'all',
    when: (ctx) => ctx.isOptInOptOutEnabled || ctx.isCrmImportFormsFeatureEnabled,
  },
  {
    section: SETTINGS_SECTION.orgDirectory,
    testId: 'settings-nav-org-directory',
    labelKey: 'publisher.left_navigation.people_directory',
    headerKey: 'publisher.personal_settings.navigation.org_directory',
    icon: 'users',
    visibility: 'all',
    when: (ctx) => !ctx.isOwnerOrAdmin && !ctx.isGroupAdmin,
  },
  {
    section: SETTINGS_SECTION.security,
    testId: 'settings-nav-privacy',
    labelKey: 'publisher.personal_settings.navigation.privacy',
    icon: 'lock',
    visibility: 'all',
  },
]

/**
 * Ordered org admin nav. `visibility: 'admin'` documents IA; `when` applies feature/role rules.
 */
export const SETTINGS_MODAL_ADMIN_NAV: readonly SettingsModalAdminNavRow[] = [
  {
    section: SETTINGS_SECTION.adminOrganization,
    testId: 'settings-nav-admin-organization',
    labelKey: 'publisher.admin.navigation.workspace',
    headerKey: 'publisher.admin.workspace_preferences.page_title',
    icon: 'organization',
    visibility: 'admin',
    when: (ctx) => ctx.features.includes('organization_branding')
      && (ctx.isOwnerOrAdmin || (ctx.isGroupAdmin && ctx.isGroupBrandingFeatureEnabled)),
  },
  {
    section: SETTINGS_SECTION.adminDashboard,
    testId: 'settings-nav-admin-dashboard',
    labelKey: 'publisher.admin.dashboard.title',
    icon: 'dashboard',
    visibility: 'admin',
    when: (ctx) => ctx.hasAdminDashboard,
  },
  {
    section: SETTINGS_SECTION.adminPeople,
    testId: 'settings-nav-admin-people',
    labelKey: 'publisher.admin.navigation.people',
    headerKey: 'publisher.admin.navigation.people',
    icon: 'users',
    visibility: 'admin',
    when: (ctx) => ctx.isOwnerOrAdmin || ctx.isGroupAdmin,
  },
  {
    section: SETTINGS_SECTION.adminAccess,
    testId: 'settings-nav-admin-access',
    labelKey: 'publisher.admin.navigation.access',
    icon: 'access',
    visibility: 'admin',
    when: (ctx) => ctx.isOwnerOrAdmin,
  },
  {
    section: SETTINGS_SECTION.adminSecurity,
    testId: 'settings-nav-admin-security',
    labelKey: 'publisher.admin.navigation.security',
    icon: 'admin-lock',
    visibility: 'admin',
    when: (ctx) => ctx.isOwnerOrAdmin,
  },
  {
    section: SETTINGS_SECTION.adminBilling,
    testId: 'settings-nav-admin-billing',
    labelKey: 'publisher.admin.navigation.billing',
    icon: 'billing',
    visibility: 'admin',
    when: (ctx) => ctx.isOwnerOrAdmin,
  },
  {
    section: SETTINGS_SECTION.adminManagedEventTypes,
    testId: 'settings-nav-admin-managed-event-types',
    labelKey: 'publisher.admin.navigation.managed_events',
    icon: 'managed-events',
    visibility: 'admin',
    when: (ctx) => ctx.isOwnerOrAdmin || ctx.isGroupAdmin,
  },
]

/** Sub-tabs for `?settings=calendar` (memory routes under `/app/availability`). */
export const SETTINGS_MODAL_CALENDAR_SUBNAV: readonly { slug: 'calendar' | 'availability' | 'advanced' }[] = [
  { slug: 'calendar' },
  { slug: 'availability' },
  { slug: 'advanced' },
]

/** Slugs for People sub-tabs — order matches {@link features/users_page/switcher} and `?settings=` people sections. */
export type SettingsAdminPeopleSubnavSlug = 'users' | 'invitations' | 'migrations'
export type SettingsAdminPeopleSettingsTab = 'active_users' | 'pending_users' | 'migrating_users'

export type SettingsOrgDirectorySubnavSlug = SettingsAdminPeopleSubnavSlug
export type SettingsOrgDirectorySettingsTab = SettingsAdminPeopleSettingsTab

/**
 * Sub-tabs for admin People (active / pending / migrating members).
 * Used by the Users area switcher (standalone + settings modal).
 * Tab labels live in {@link features/users_page/switcher} (`translatePeopleSubnavLabel`), matching calendar subnav.
 * Filter with `onlyWithAppConsolidation` where noted.
 */
export const SETTINGS_ADMIN_PEOPLE_SUBNAV: readonly {
  slug: SettingsAdminPeopleSubnavSlug
  section: SettingsSection
  settingsTab: SettingsAdminPeopleSettingsTab
  onlyWithAppConsolidation?: boolean
}[] = [
  { slug: 'users', section: SETTINGS_SECTION.adminPeople, settingsTab: 'active_users' },
  { slug: 'invitations', section: SETTINGS_SECTION.adminPeople, settingsTab: 'pending_users' },
  {
    slug: 'migrations',
    section: SETTINGS_SECTION.adminPeople,
    settingsTab: 'migrating_users',
    onlyWithAppConsolidation: true,
  },
]

/** Sub-tabs for org directory (active / pending / migrating members) — same slugs as admin People member tabs. */
export const SETTINGS_ORG_DIRECTORY_SUBNAV: readonly {
  slug: SettingsOrgDirectorySubnavSlug
  section: SettingsSection
  settingsTab: SettingsOrgDirectorySettingsTab
  onlyWithAppConsolidation?: boolean
}[] = [
  { slug: 'users', section: SETTINGS_SECTION.orgDirectory, settingsTab: 'active_users' },
  { slug: 'invitations', section: SETTINGS_SECTION.orgDirectory, settingsTab: 'pending_users' },
  {
    slug: 'migrations',
    section: SETTINGS_SECTION.orgDirectory,
    settingsTab: 'migrating_users',
    onlyWithAppConsolidation: true,
  },
]

export type AccessSubnavSlug =
  | 'domain_control'
  | 'user_access'
  | 'sso'
  | 'user_provisioning'
  | 'activity_log'

export function buildAccessSubnavSlugs(joinAnOrgNewFlowEnabled: boolean): AccessSubnavSlug[] {
  if (joinAnOrgNewFlowEnabled) {
    return ['user_access', 'sso', 'user_provisioning', 'activity_log']
  }
  return ['domain_control', 'sso', 'user_provisioning', 'activity_log']
}

const SECTION_ICON_FROM_NAV: Partial<Record<string, SettingsIcon>> = (() => {
  const m: Partial<Record<string, SettingsIcon>> = {}
  for (const row of SETTINGS_MODAL_ACCOUNT_NAV) {
    m[row.section] = row.icon
  }
  for (const row of SETTINGS_MODAL_ADMIN_NAV) {
    m[row.section] = row.icon
  }
  return m
})()

/**
 * Icon id for a `?settings=` section value. People sub-routes share the Users icon.
 */
export function getSettingsIconIdForSection(section: string): SettingsIcon {
  if (isSettingsAdminPeopleSection(section) || section === SETTINGS_SECTION.orgDirectory) {
    return 'users'
  }
  return SECTION_ICON_FROM_NAV[section] ?? 'gear'
}

/**
 * I18n key for the settings modal page title when built nav items do not supply a label
 * ({@link getSettingsSectionTitle} falls back to `I18n.t` of this key).
 * Uses the same nav rows as the sidebar; optional `headerKey` overrides `labelKey` for the header only.
 */
export function getSettingsSectionTitleI18nKey(section: string): string {
  if (isSettingsAdminPeopleSection(section)) {
    const peopleRow = SETTINGS_MODAL_ADMIN_NAV.find((r) => r.section === SETTINGS_SECTION.adminPeople)
    return peopleRow ? (peopleRow.headerKey ?? peopleRow.labelKey) : 'publisher.admin.navigation.people'
  }
  if (section === SETTINGS_SECTION.orgDirectory) {
    const orgRow = SETTINGS_MODAL_ACCOUNT_NAV.find((r) => r.section === SETTINGS_SECTION.orgDirectory)
    return orgRow ? (orgRow.headerKey ?? orgRow.labelKey) : 'publisher.personal_settings.navigation.org_directory'
  }
  const account = SETTINGS_MODAL_ACCOUNT_NAV.find((r) => r.section === section)
  if (account) {
    return account.headerKey ?? account.labelKey
  }
  if (section === SETTINGS_SECTION.login_preferences) {
    return 'publisher.personal_settings.navigation.login_preferences'
  }
  const admin = SETTINGS_MODAL_ADMIN_NAV.find((r) => r.section === section)
  if (admin) {
    return admin.headerKey ?? admin.labelKey
  }
  const profileRow = SETTINGS_MODAL_ACCOUNT_NAV.find((r) => r.section === SETTINGS_SECTION.profile)
  return profileRow?.labelKey ?? 'publisher.personal_settings.navigation.profile'
}

export type SettingsNavRowForTitle = {
  section: string
  label: string
}

/**
 * Legacy admin center URL segments (under `useRouteMatch().url`) → `?settings=` values.
 * Paths are `${adminUrl}/${segment}`; keep in sync with memory `basePath` in the pane registry.
 */
export const LEGACY_ADMIN_EXACT_SEGMENTS: ReadonlyArray<readonly [string, string]> = [
  ['dashboard', SETTINGS_SECTION.adminDashboard],
  ['login', SETTINGS_SECTION.adminAccess],
  ['billing', SETTINGS_SECTION.adminBilling],
  ['branding', SETTINGS_SECTION.adminOrganization],
  ['security', SETTINGS_SECTION.adminSecurity],
  ['managed_event_types', SETTINGS_SECTION.adminManagedEventTypes],
]

/** Prefix match: `/notetaker` and nested paths → AI settings Notetaker access sub-tab. */
export const LEGACY_ADMIN_PREFIX_SEGMENTS: ReadonlyArray<readonly [string, string]> = [
  ['notetaker', SETTINGS_SECTION.ai_notetaker],
]

/**
 * Legacy personal center URL segments (under `/app/personal`) → `?settings=` values.
 * Prefix segments support tabbed memory routes (`/profile/general`, `/security/booking`, …).
 * Keep in sync with memory `basePath` entries in the pane registry.
 */
export const LEGACY_PERSONAL_PREFIX_SEGMENTS: ReadonlyArray<readonly [string, string]> = [
  ['profile', SETTINGS_SECTION.profile],
  ['security', SETTINGS_SECTION.security],
]

export const LEGACY_PERSONAL_EXACT_SEGMENTS: ReadonlyArray<readonly [string, string]> = [
  ['branding', SETTINGS_SECTION.branding],
  ['link', SETTINGS_SECTION.link],
  ['login_preferences', SETTINGS_SECTION.login_preferences],
  ['workflows', SETTINGS_SECTION.workflows],
  ['ai_notetaker', SETTINGS_SECTION.ai_notetaker],
  ['assistant', SETTINGS_SECTION.assistant],
  ['contacts_settings', SETTINGS_SECTION.contacts_settings],
]

/** Paths under `…/invitations/*` that are standalone app routes, not settings deep links. */
export const INVITATIONS_NON_LEGACY_SUBPATHS: ReadonlySet<string> = new Set(['new'])
